import React, { useState, useEffect } from 'react';
import {
  Grid,
  Menu,
  MenuItem,
  FormControlLabel,
  ListSubheader,
  IconButton,
  Divider,
  Box,
  useTheme,
} from '@mui/material';
import {
  getDocumentsLoaded,
  getInternalAnnotationVisibility,
  getOCRHasRun,
  getShowLiveText,
  inspection,
  app,
  getShowDifferenceBoxes,
  getHideOtherDifferences,
  getShowOverlay,
  getShowTextHighlights,
  getGrayscaleMode,
  getSinglePageView,
  getShowRepeats,
  getIsStepAndRepeat,
  getSelectedTool,
  getIsSingleFile,
} from 'store';
import { useDispatch, useSelector } from 'react-redux';
import { PDFTronTools } from 'types';
import GVTooltip from 'components/lib/GVToolTip/GVTooltip';
import GVSwitch from 'components/lib/GVSwitch/GVSwitch';
import GVToggleButton from '../GVToggleButton/GVToggleButton';
import { isResultUrl } from 'utils/location';
import DisplaySettingsIcon from 'components/icons/DisplaySettingsIcon/DisplaySettingsIcon';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import KeyboardShortcutsIcon from 'components/icons/KeyboardShortcutsIcon/KeyboardShortcutsIcon';
import { makeStyles } from 'tss-react/mui';
import { getStyleVariables } from 'styles/vars';
import TextHighlightIcon from 'components/icons/TextHighlightIcon/TextHighlightIcon';
import TextHighlightModal from './TextHighlightModal';
import KeyboardShortcutsModal from './KeyboardShortcutsModal';
import GraphicModeControl from './GraphicModeControl';
import styles from './CogMenu.module.css';
import { persistDisplaySetting } from 'store/app/persistDisplaySetting';
interface CogProps {
  loaded: boolean | undefined;
}

const useStyles = makeStyles()(() => ({
  switch: {
    marginRight: 8,
  },
}));

const CogMenu = (props: CogProps) => {
  const [selected, setSelected] = useState<boolean>(false);
  const internalAnnotationVisibility = useSelector(getInternalAnnotationVisibility);
  const showDifferenceBoxes = useSelector(getShowDifferenceBoxes);
  const hideOtherDifferences = useSelector(getHideOtherDifferences);
  const showOverlay = useSelector(getShowOverlay);
  const ocrHasRun = useSelector(getOCRHasRun);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const hasLoaded = useSelector(getDocumentsLoaded);
  const liveText = useSelector(getShowLiveText);
  const textHighlights = useSelector(getShowTextHighlights);
  const grayscaleMode = useSelector(getGrayscaleMode);
  const singlePageView = useSelector(getSinglePageView);
  const showRepeats = useSelector(getShowRepeats);
  const isStepAndRepeat = useSelector(getIsStepAndRepeat);
  const dispatch = useDispatch();
  const { classes } = useStyles();
  const theme = useTheme();
  const styleVariables = getStyleVariables(theme);
  const [textHighlightModalOpen, setTextHighlightModalOpen] = useState(false);
  const [keyboardShortcutsOpen, setKeyboardShortcutsOpen] = useState(false);
  const selectedTool = useSelector(getSelectedTool);
  const isSingleFile = useSelector(getIsSingleFile);
  const isDisabledForResync = selectedTool === PDFTronTools.RESYNC;

  const toggleSelected = (event: React.MouseEvent<HTMLElement, MouseEvent>, value: any) => {
    setSelected(!selected);
    setAnchorEl(event.currentTarget);
  };

  const handleLiveTextSwitch = () => {
    dispatch(app.actions.setShowLiveText(!liveText));
  };

  const handleTextHighlightSwitch = () => {
    const next = !textHighlights;
    dispatch(app.actions.setShowTextHighlights(next));
    persistDisplaySetting('showTextHighlights', next);
  };

  const handleInternalAnnotationSwitch = () => {
    const next = !internalAnnotationVisibility;
    dispatch(inspection.actions.setDefaultInternalAnnotationsVisibility(next));
    persistDisplaySetting('showAnnotations', next);
  };

  const handleDifferenceBoxSwitch = () => {
    const next = !showDifferenceBoxes;
    dispatch(app.actions.setShowDifferenceBoxes(next));
    persistDisplaySetting('showDifferenceBoxes', next);
  };

  const handleHideOtherDifferencesSwitch = () => {
    const next = !hideOtherDifferences;
    dispatch(app.actions.setHideOtherDifferences(next));
    persistDisplaySetting('hideOtherDifferences', next);
  };

  const handleOverlaySwitch = () => {
    const next = !showOverlay;
    dispatch(app.actions.setShowOverlay(next));
    persistDisplaySetting('showOverlay', next);
  };

  const handleGrayscaleModeSwitch = () => {
    const next = !grayscaleMode;
    dispatch(app.actions.setGrayscaleMode(next));
    persistDisplaySetting('grayscaleMode', next);
  };

  const handleSinglePageViewSwitch = () => {
    const next = !singlePageView;
    dispatch(app.actions.setSinglePageView(next));
    persistDisplaySetting('singlePageView', next);
  };

  const handleShowRepeatsSwitch = () => {
    const next = !showRepeats;
    dispatch(app.actions.setShowRepeats(next));
    persistDisplaySetting('showRepeats', next);
  };

  const handleClose = () => {
    setSelected(false);
  };

  const handleOpenTextHighlightModal = (event: React.MouseEvent) => {
    event.stopPropagation(); // Prevent the menu item click from closing the menu
    setTextHighlightModalOpen(true);
    handleClose();
  };

  const handleCloseTextHighlightModal = () => {
    setTextHighlightModalOpen(false);
  };

  const handleOpenKeyboardShortcuts = (event: React.MouseEvent) => {
    event.stopPropagation(); // Prevent the menu item click from bubbling
    setKeyboardShortcutsOpen(true);
    handleClose();
  };

  const handleCloseKeyboardShortcuts = () => {
    setKeyboardShortcutsOpen(false);
  };

  useEffect(() => {
    if (!hasLoaded) {
      dispatch(app.actions.setShowLiveText(false));
    }
  }, [dispatch, hasLoaded]);

  useEffect(() => {
    if (hasLoaded && ocrHasRun) {
      dispatch(app.actions.setShowLiveText(true));
    }
  }, [dispatch, hasLoaded, ocrHasRun]);

  return (
    <Grid container alignItems="center">
      <GVToggleButton value="cogMenu" id="cogMenu" small selected={selected} onChange={toggleSelected}>
        <GVTooltip title="Settings" side="top" className={styles.tooltip}>
          <DisplaySettingsIcon sx={{ fontSize: 14 }} />
        </GVTooltip>
      </GVToggleButton>

      <Menu
        open={selected}
        keepMounted
        anchorEl={anchorEl}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        onClose={handleClose}
        sx={{
          '& .MuiPaper-root': {
            marginTop: '6px',
            backgroundColor: '#303236',
            ...styleVariables.scrollBar,
            scrollbarGutter: 'auto',
          },
          '& .MuiMenu-list': {
            padding: '12px 8px',
          },
          '& .MuiMenuItem-root': {
            minHeight: '28px',
            borderRadius: '4px',
            padding: '0 8px',
            backgroundColor: 'transparent',
            transition: 'background-color 150ms ease',
            '& .MuiTouchRipple-root': {
              display: 'none',
            },
            '&:hover': {
              backgroundColor: 'rgba(0, 0, 0, 0.2)',
            },
          },
          '& .MuiFormControlLabel-root': {
            marginLeft: 0,
          },
          '& .MuiFormControlLabel-label': {
            fontWeight: 600,
          },
          '& .MuiMenuItem-root + .MuiDivider-root': {
            marginTop: '8px',
            marginBottom: '8px',
          },
        }}
      >
        <ListSubheader
          sx={{
            background: 'none',
            padding: '0 8px 8px',
            fontSize: '12px',
            fontWeight: 500,
            lineHeight: '16px',
            color: '#8792AB',
          }}
        >
          Display settings
        </ListSubheader>
        <MenuItem data-testid="cog_show_live_text">
          <FormControlLabel
            control={
              <GVSwitch
                checked={liveText}
                onChange={handleLiveTextSwitch}
                disabled={!props.loaded}
                className={classes.switch}
              />
            }
            label="Live text"
          />
        </MenuItem>
        <MenuItem data-testid="cog_show_annotations">
          <FormControlLabel
            control={
              <GVSwitch
                checked={internalAnnotationVisibility}
                onChange={handleInternalAnnotationSwitch}
                disabled={!props.loaded}
                className={classes.switch}
              />
            }
            label="Annotations"
          />
        </MenuItem>
        {isResultUrl() && isStepAndRepeat && (
          <MenuItem data-testid="cog_show_repeats">
            <FormControlLabel
              control={
                <GVSwitch
                  checked={showRepeats}
                  onChange={handleShowRepeatsSwitch}
                  disabled={!props.loaded}
                  className={classes.switch}
                />
              }
              label="Show all repeats"
            />
          </MenuItem>
        )}
        <MenuItem data-testid="cog_show_grayscale_mode">
          <FormControlLabel
            control={
              <GVSwitch checked={grayscaleMode} onChange={handleGrayscaleModeSwitch} className={classes.switch} />
            }
            label="Grayscale mode"
          />
        </MenuItem>
        <MenuItem data-testid="cog_single_page_view">
          <FormControlLabel
            control={
              <GVSwitch
                checked={singlePageView}
                onChange={handleSinglePageViewSwitch}
                disabled={!props.loaded}
                className={classes.switch}
              />
            }
            label="Page-by-page view"
          />
        </MenuItem>
        {isResultUrl() && !isSingleFile && (
          <MenuItem data-testid="cog_show_text_highlights">
            <GVTooltip
              title="Disabled while Re-Inspect tool is active"
              side="top"
              className={styles.tooltip}
              disableHoverListener={!isDisabledForResync}
            >
              <span>
                <FormControlLabel
                  control={
                    <GVSwitch
                      checked={textHighlights}
                      onChange={handleTextHighlightSwitch}
                      disabled={!props.loaded || isDisabledForResync}
                      className={classes.switch}
                    />
                  }
                  label="Text highlights"
                />
              </span>
            </GVTooltip>
            <GVTooltip title="Highlight compared content to aid in faster text reviews" className={styles.tooltip}>
              <InfoOutlinedIcon sx={{ fontSize: '16px', marginLeft: '-7px' }} />
            </GVTooltip>

            {textHighlights && (
              <GVTooltip title="Edit colors" side="bottom" className={styles.tooltip}>
                <IconButton
                  data-testid="text-highlight-edit-color-button"
                  sx={{ marginLeft: '8px', padding: 0 }}
                  onClick={handleOpenTextHighlightModal}
                >
                  <TextHighlightIcon height={'12px'} width={'12px'} />
                </IconButton>
              </GVTooltip>
            )}
          </MenuItem>
        )}
        {isResultUrl() && (
          <MenuItem data-testid="cog_show_difference_boxes">
            <GVTooltip
              title="Disabled while Re-Inspect tool is active"
              side="top"
              className={styles.tooltip}
              disableHoverListener={!isDisabledForResync}
            >
              <span>
                <FormControlLabel
                  control={
                    <GVSwitch
                      checked={showDifferenceBoxes}
                      onChange={handleDifferenceBoxSwitch}
                      disabled={!props.loaded || isDisabledForResync}
                      className={classes.switch}
                    />
                  }
                  label="Difference boxes"
                />
              </span>
            </GVTooltip>
          </MenuItem>
        )}
        {isResultUrl() && (
          <MenuItem data-testid="cog_hide_other_differences">
            <GVTooltip
              title="Disabled while Re-Inspect tool is active"
              side="top"
              className={styles.tooltip}
              disableHoverListener={!isDisabledForResync}
            >
              <span>
                <FormControlLabel
                  control={
                    <GVSwitch
                      checked={hideOtherDifferences}
                      onChange={handleHideOtherDifferencesSwitch}
                      disabled={!props.loaded || isDisabledForResync}
                      className={classes.switch}
                    />
                  }
                  label="Hide other differences"
                />
              </span>
            </GVTooltip>
            <GVTooltip
              title="When a difference is selected, hide every other difference box"
              side="top"
              className={styles.tooltip}
            >
              <InfoOutlinedIcon sx={{ fontSize: '16px', marginLeft: '-7px' }} />
            </GVTooltip>
          </MenuItem>
        )}
        {isResultUrl() && (
          <MenuItem data-testid="cog_show_overlay">
            <FormControlLabel
              control={
                <GVSwitch
                  checked={showOverlay}
                  onChange={handleOverlaySwitch}
                  disabled={!props.loaded}
                  className={classes.switch}
                />
              }
              label="Overlay"
            />
            <GVTooltip title="The gray background that surrounds differences" side="top" className={styles.tooltip}>
              <InfoOutlinedIcon sx={{ fontSize: '16px', marginLeft: '-7px' }} />
            </GVTooltip>
          </MenuItem>
        )}
        {isResultUrl() && <GraphicModeControl loaded={props.loaded} />}
        {isResultUrl() && <Divider sx={{ borderColor: 'var(--gds-color-cool-gray-450)' }} />}
        {isResultUrl() && (
          <MenuItem data-testid="cog_keyboard_shortcuts" onClick={handleOpenKeyboardShortcuts} sx={{ fontWeight: 600 }}>
            <Box
              sx={{ width: 28, marginRight: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
            >
              <KeyboardShortcutsIcon sx={{ fontSize: 18 }} />
            </Box>
            Keyboard shortcuts
          </MenuItem>
        )}
      </Menu>

      <TextHighlightModal open={textHighlightModalOpen} onClose={handleCloseTextHighlightModal} />
      <KeyboardShortcutsModal open={keyboardShortcutsOpen} onClose={handleCloseKeyboardShortcuts} />
    </Grid>
  );
};

export default CogMenu;
